/* eslint-disable no-shadow */
import React, { useEffect, useMemo, useState } from 'react';
import { bool, func, shape, string, oneOf } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { get } from 'lodash';
import { parse, format } from 'date-fns';
import { Divider, Text } from '@landtrustinc/design-system';
import ActivityFeed from '../ActivityFeed/ActivityFeed';
import routeConfiguration from '../../routeConfiguration';
import { updateRecentPackages } from '../../util/localStorage';
import {
  getListingImageCaptions,
  getPackageImages,
  getPackage,
  listingStateLinks,
  getCampsites,
  getLodging,
  getLodgingPricesOptions,
} from '../../util/listing';
import { hasPremiumMembership, getFirstName } from '../../util/user';
import {
  LODGING_INFO,
  getStartingPrice,
  getTransactionFromInquiry,
  checkPackageAvailability,
  isPackageOnWaitingList,
  getPackageLodgingPrices,
  isEarlyAccessAvailable,
  getFlexiblePricingData,
} from '../../util/package';
import { LISTING_STATE_PENDING_APPROVAL, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
  formatNumber,
} from '../../util/data';
import {
  huntingFaqs,
  outdoorRecFaqs,
  HUNT_ACTIVITY,
  OUTDOOR_RECREATION_ACTIVITY,
} from '../../marketplace-custom-config';
import { parseMoneyObjString } from '../../util/currency';
import { listingSelectionToInquiryState } from '../../util/inquirySelection';
import { buildPackageStructuredData } from '../../util/structuredData';
import { SUMMARY } from '../../util/editListing';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck';
import {
  toggleFavoritePackages,
  updateWaitingList,
  followSubject,
  unfollowSubject,
  updateFollowedSubject,
  fetchFollowedSubjects,
} from '../../ducks/user.duck';
import { fetchTransaction } from '../../ducks/Transaction.duck';
import {
  CredovaPlugin,
  EarlyAccessBar,
  NamedRedirect,
  PackageQuantityBadge,
  PackagePricingPanel,
  PackageAvailabilityModal,
  SectionFAQ,
  EarlyAccessModal,
  BuckChat,
  PackageBadges,
} from '../../components';
import { PACKAGE_QUANTITY_BADGE_VARIANTS } from '../../components/PackageQuantityBadge/PackageQuantityBadge';
import { Container } from '../../components/Layout';
import { STEP_PARTY } from '../../components/InquiryWizard/InquiryWizardContext';
import PackageNotFoundPage from '../PackageNotFoundPage/PackageNotFoundPage';
import {
  loadData,
  setInitialValues,
  fetchPackageActivityFeed,
  fetchActivityLogLikes,
} from '../ListingPage/ListingPage.duck';
import SectionImages from '../ListingPage/SectionImages';
import SectionDescriptionMaybe from '../ListingPage/SectionDescriptionMaybe';
import SectionHostMaybe from '../ListingPage/SectionHostMaybe';
import MobileCheckAvailability from './MobileCheckAvailability';
import SectionLandDetails from '../ListingPage/SectionLandDetails';

import SectionPropertyRules from '../ListingPage/SectionPropertyRules';
import SectionPropertyAccess from '../ListingPage/SectionPropertyAccess';
import SectionMapMaybe from '../ListingPage/SectionMapMaybe';
import ShareButton from '../ListingPage/ShareButton';
import SectionWhatsIncluded from '../ListingPage/SectionWhatsIncluded';
import SectionAddons from '../ListingPage/SectionAddons';
import SectionHuntingInformation from './SectionHuntingInformation';
import SEOBreadcrumbs from '../../components/SEOBreadcrumbs/SEOBreadcrumbs';

import css from './PackagePage.module.css';
import FavoritePackage from '../../components/FavoritePackage/FavoritePackage';
import CredovaModal from '../../components/CredovaPlugin/CredovaModal';
import SectionReviews from '../ListingPage/SectionReviews';
import PackageHeaderSection from './components/PackageHeaderSection';
import PackagePageWrapper from './components/PackagePageWrapper';
import config from '../../config';

const { UUID } = sdkTypes;

export const PackagePageComponent = ({
  currentUser,
  currentUserInit,
  getListing,
  getOwnListing,
  intl,
  onManageDisableScrolling,
  params: rawParams,
  location,
  scrollingDisabled,
  reviews,
  customerReviews,
  reviewsMeta,
  showListingError,
  timeSlots,
  onToggleFavoritePackage,
  onFetchTransaction,
  transaction,
  transactionIdFromInquiry,
  history,
  onUpdateWaitingList,
  isOnWaitingList,
  updatingWaitingListInProgress,
  activityPackageFeedData,
  fetchActivityFeedData,
  followedSubjects,
  fetchFollowedSubjectsData,
  followSubjectMutation,
  unfollowSubjectMutation,
  updateFollowedSubjectMutation,
  fetchTotalLikesData,
  isFetchingReviews,
}) => {
  const [isPackageAvailabilityModalOpen, setIsPackageAvailabilityModalOpen] = useState(false);
  const [isEarlyAccessModalOpen, setIsEarlyAccessModalOpen] = useState(false);
  const [isCredovaModalOpen, setIsCredovaModalOpen] = useState(false);
  const [estimatedTotalPrice, setEstimatedTotalPrice] = useState(0);
  const [bookingSelection, setBookingSelection] = useState(null);

  const onContactUser = (isOwnListing, selection) => {
    const listingId = new UUID(rawParams.id);
    const currentListing = ensureListing(getListing(listingId));
    const currentPackage = getPackage(currentListing, rawParams.packageId);

    const { activity } = currentPackage || {};
    const parsedPackages = {
      ...currentPackage,
      price: parseMoneyObjString(currentPackage.price),
      lodgingPrice: parseMoneyObjString(currentPackage.lodgingPrice),
    };

    if (isOwnListing) {
      // ..
    } else if (transactionIdFromInquiry) {
      history.push(
        createResourceLocatorString('OrderPage', routeConfiguration(), {
          id: transactionIdFromInquiry,
        })
      );
    } else {
      const picked = listingSelectionToInquiryState(
        selection === undefined ? bookingSelection : selection
      );
      history.push(
        createResourceLocatorString('NewInquiryPage', routeConfiguration(), {
          ...rawParams,
          step: STEP_PARTY.path,
        }),
        {
          activity,
          selectedPackage: parsedPackages,
          entry: 'package',
          ...picked,
        }
      );
    }
  };
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const lodgingPricesOptions = getLodgingPricesOptions(currentListing, intl);
  const currentPackage = getPackage(currentListing, rawParams.packageId);

  const { optionalLodging, includedLodging } = getPackageLodgingPrices(
    currentPackage,
    lodgingPricesOptions
  );
  const campsites = getCampsites(currentListing);

  const lodging = getLodging(currentListing);
  const includedLodgingMerged = [];
  const optionalLodgingMerged = [];

  const { lodgingPrices = [] } = currentPackage || {};

  lodgingPrices.forEach((item, index) => {
    if (item.option === LODGING_INFO.REQUIRED) {
      includedLodgingMerged.push({ ...item, ...lodgingPricesOptions[index] });
    }
  });

  lodgingPrices.forEach((item, index) => {
    if (item.option === LODGING_INFO.OPTIONAL) {
      optionalLodgingMerged.push({ ...item, ...lodgingPricesOptions[index] });
    }
  });

  const mergedLodgingPrices = [...includedLodgingMerged, ...optionalLodgingMerged];

  useEffect(() => {
    if (transactionIdFromInquiry) {
      onFetchTransaction(new UUID(transactionIdFromInquiry));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionIdFromInquiry]);

  const {
    activity,
    title: packageTitle,
    description: packageDescription,
    availableDateFrom,
    earlyAccessExpiration,
  } = currentPackage || {};

  const { bookingRangeIsFuture, bookingRangeIsPast, isAvailable } = checkPackageAvailability(
    currentPackage
  );

  const date = parse(availableDateFrom);
  const formattedPackageAvailableDate = format(date, 'MMMM D, YYYY');

  const outOfBookingRange = bookingRangeIsFuture || bookingRangeIsPast;

  const userHasPremiumMembership = useMemo(() => hasPremiumMembership(currentUser), [currentUser]);
  const isEarlyAccess = useMemo(
    () => isEarlyAccessAvailable(earlyAccessExpiration) && isAvailable && !outOfBookingRange,
    [earlyAccessExpiration, isAvailable, outOfBookingRange]
  );
  const isBlockedByEarlyAccess = useMemo(() => {
    if (currentUserInit) {
      return !userHasPremiumMembership && isEarlyAccess;
    }

    return false;
  }, [currentUserInit, isEarlyAccess, userHasPremiumMembership]);

  const packageMinParty = get(currentPackage, 'guests.min', 1);
  const flexiblePricingData = getFlexiblePricingData(
    currentPackage?.flexiblePricing,
    packageMinParty
  );

  const startingPrice = getStartingPrice({
    ...currentPackage,
    optionalLodging,
    includedLodging,
    flexiblePricing: flexiblePricingData,
  });

  const isHunting = activity === HUNT_ACTIVITY;
  const isOutdoorRec = activity === OUTDOOR_RECREATION_ACTIVITY;
  const faqsToRender = isOutdoorRec ? outdoorRecFaqs : huntingFaqs;

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };
  const listingType = isDraftVariant ? LISTING_PAGE_PARAM_TYPE_DRAFT : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = SUMMARY;

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;
  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  useEffect(() => {
    const packageID = rawParams.packageId;
    const listingID = listingId.uuid;

    if (packageID && listingID) {
      updateRecentPackages(listingID, packageID);
    }
  }, [rawParams.packageId, listingId.uuid]);

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const { geolocation = null, title, publicData, metadata } = currentListing.attributes;

  if (showListingError) {
    // Other error in fetching listing

    const errorTitle = intl.formatMessage({
      id: 'ListingPage.errorLoadingListingTitle',
    });

    return (
      <PackagePageWrapper title={errorTitle} scrollingDisabled={scrollingDisabled}>
        <p>
          <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
        </p>
      </PackagePageWrapper>
    );
  }

  if (!currentListing.id) {
    // Still loading the listing

    const loadingTitle = intl.formatMessage({
      id: 'ListingPage.loadingListingTitle',
    });

    return (
      <PackagePageWrapper title={loadingTitle} scrollingDisabled={scrollingDisabled}>
        <p>
          <FormattedMessage id="ListingPage.loadingListingMessage" />
        </p>
      </PackagePageWrapper>
    );
  }

  if (!currentPackage || !currentPackage.isPublished) {
    // Unpublished packages stay out of search and direct URLs. Sharetribe
    // cannot restrict them, so the public page is the same as a missing package.

    return <PackageNotFoundPage />;
  }

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;
  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');
  const authorFirstName = getFirstName(authorDisplayName);

  const packagesImages = getPackageImages(currentListing, currentPackage?.id);

  const headerImages = packagesImages.length ? packagesImages : currentListing.images;
  const listingImageCaptions = getListingImageCaptions(currentListing);

  const getSchemaImages = variantName =>
    (headerImages || [])
      .map(image => {
        const { variants } = image.attributes;
        const variant = variants ? variants[variantName] : null;

        // deprecated
        // for backwards compatibility only
        const { sizes } = image.attributes;
        const size = sizes ? sizes.find(i => i.name === variantName) : null;

        return variant || size;
      })
      .filter(variant => variant != null);

  const activityData = get(publicData, `activities.${activity}`, {});
  const landDetails = get(activityData, 'landDetails', null);

  const nearestTown = get(publicData, 'nearestTown.selectedPlace.address', null);
  const propertyVisit = get(publicData, 'propertyVisit', null);
  const rawAcreage = get(publicData, 'location.acreage', 0);
  const acreage = rawAcreage ? formatNumber(rawAcreage) : null;

  const usState = get(currentListing, 'attributes.publicData.location.usState', null);
  const usCounty = get(currentListing, 'attributes.publicData.location.usCounty', null);
  const facebookImages = getSchemaImages('facebook');
  const twitterImages = getSchemaImages('twitter');
  const schemaTitle = intl.formatMessage(
    { id: 'PackagePage.schemaTitle' },
    {
      packageTitle,
      title,
      usState,
    }
  );
  const schema = buildPackageStructuredData({
    listing: currentListing,
    package: currentPackage,
    intl,
  });
  const { licenseLink, regulationsLink } = listingStateLinks(currentListing);

  const BLOCK_REGULATIONS_FOR_STATES = [
    'Alabama',
    'Wisconsin',
    'Illinois',
    'Mississippi',
    'Georgia',
  ];
  const shouldShowRegulationsLink = usState
    ? !BLOCK_REGULATIONS_FOR_STATES.includes(usState)
    : true;

  const landownerName = get(currentListing, 'author.attributes.profile.displayName', 'Landowner');

  // "Sold Out" if past or fully booked, "Accepting Bookings {date}" if future, null otherwise.
  const renderAvailabilityBadges = isShortVersion => {
    let badge;

    if (!isAvailable || bookingRangeIsPast) {
      badge = {
        variant: PACKAGE_QUANTITY_BADGE_VARIANTS.DANGER,
        text: isShortVersion ? (
          <FormattedMessage
            id="PackagePricingPanel.soldOutSimple"
            values={{ date: formattedPackageAvailableDate }}
          />
        ) : (
          <FormattedMessage id="PackagePage.soldOutInquiry" values={{ landowner: landownerName }} />
        ),
      };
    } else if (bookingRangeIsFuture) {
      badge = {
        variant: PACKAGE_QUANTITY_BADGE_VARIANTS.WARNING,
        text: isShortVersion ? (
          <FormattedMessage
            id="PackagePricingPanel.outOfRange"
            values={{ date: formattedPackageAvailableDate }}
          />
        ) : (
          <FormattedMessage
            id="PackagePage.outOfBookingRange"
            values={{ date: formattedPackageAvailableDate, landowner: landownerName }}
          />
        ),
      };
    }

    if (!badge) {
      return null;
    }

    return (
      <div>
        <PackageQuantityBadge variant={badge.variant} size="large" text={badge.text} />
      </div>
    );
  };
  const requiredLodgingPrices = mergedLodgingPrices.filter(
    item => item.option === LODGING_INFO.REQUIRED
  );
  const optionalLodgingPrices = mergedLodgingPrices.filter(
    item => item.option === LODGING_INFO.OPTIONAL
  );

  const seoBreadcrumbs =
    usState && usCounty
      ? [
          <a href="/" target="_blank" rel="noreferrer">
            <FormattedMessage id="ListingPage.BreadcrumbsHome" />{' '}
          </a>,
          <a href="/us" target="_blank" rel="noreferrer">
            US{' '}
          </a>,
          <a href={`/us/${usState.toLowerCase()}`} target="_blank" rel="noreferrer">
            {usState}{' '}
          </a>,
          <a
            href={`/us/${usState.toLowerCase()}/${usCounty.toLowerCase().replaceAll(' ', '-')}`}
            target="_blank"
            rel="noreferrer"
          >
            {usCounty}{' '}
          </a>,
        ]
      : [];

  const handlePriceChange = price => {
    setEstimatedTotalPrice(price);
  };

  return (
    <PackagePageWrapper
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      contentType="website"
      description={packageDescription}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={schema}
      currentUser={currentUser}
      currentListing={currentListing}
      currentPackage={currentPackage}
      onToggleFavoritePackage={onToggleFavoritePackage}
      followSubjectMutation={followSubjectMutation}
      unfollowSubjectMutation={unfollowSubjectMutation}
      followedSubjects={followedSubjects}
    >
      <PackageAvailabilityModal
        id="package-pricing-availability-modal"
        isOpen={isPackageAvailabilityModalOpen}
        onClose={() => setIsPackageAvailabilityModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        isSoldOut={!isAvailable}
        isOutOfBookingRange={outOfBookingRange}
        packageAvailableDate={formattedPackageAvailableDate}
        landownerName={landownerName}
        onNotify={() => {
          onUpdateWaitingList(currentPackage.id, currentListing.id.uuid);
        }}
        isFetching={updatingWaitingListInProgress}
        isOnWaitingList={isOnWaitingList}
      />

      <div>
        <div>
          <EarlyAccessBar
            isEarlyAccess={isEarlyAccess}
            userHasPremiumMembership={userHasPremiumMembership}
            expirationTimestamp={currentPackage?.earlyAccessExpiration}
          />
        </div>

        <Container className={css.sectionImagesContainer}>
          <SectionImages
            title={title}
            listing={currentListing}
            listingImages={headerImages}
            listingImageCaptions={listingImageCaptions}
            isOwnListing={isOwnListing}
            editParams={{
              id: listingId.uuid,
              slug: listingSlug,
              type: listingType,
              tab: listingTab,
            }}
            onManageDisableScrolling={onManageDisableScrolling}
          />
          <div className={css.listingActions}>
            <PackageHeaderSection
              listing={currentListing}
              packageId={rawParams.packageId}
              listingParams={rawParams}
              reviews={customerReviews}
              reviewsMeta={reviewsMeta}
              isFetchingReviews={isFetchingReviews}
              intl={intl}
            />
            <div className={css.shareContainer} data-testid="share-container">
              <ShareButton className={css.share} buttonClassName={css.shareButton} />
              <FavoritePackage
                currentUser={currentUser}
                listing={currentListing}
                package={currentPackage}
                onUnfavoritePackage={() => onToggleFavoritePackage(currentPackage, currentListing)}
                onFavoritePackage={() => onToggleFavoritePackage(currentPackage, currentListing)}
                followSubjectMutation={followSubjectMutation}
                unfollowSubjectMutation={unfollowSubjectMutation}
                followedSubjects={followedSubjects}
                inlineButton
              />
            </div>
          </div>
        </Container>
      </div>

      <Container className={css.contentContainer}>
        <div className={css.mainContent}>
          <Divider my="var(--spacing-4)" />

          <div className={css.mobilePackagesWrapper}>
            <Text fontWeight="bold">
              <FormattedMessage id="PackagePage.priceIncludes" />
            </Text>
            <PackageBadges packageInfo={currentPackage} listing={currentListing} />
          </div>

          {/* Show availability badge when package is sold out or not yet bookable */}
          {(!isAvailable || bookingRangeIsFuture || bookingRangeIsPast) && (
            <div className={css.availabilityBadgesWrapper}>{renderAvailabilityBadges(true)}</div>
          )}

          <SectionDescriptionMaybe
            description={packageDescription}
            publicData={publicData}
            nearestTown={nearestTown}
          />

          <div className={css.mobileCredovaWrapper}>
            <CredovaPlugin price={estimatedTotalPrice} onModalOpen={setIsCredovaModalOpen} />
          </div>

          <SectionWhatsIncluded
            currentPackage={currentPackage}
            activityData={activityData}
            campsites={campsites}
            lodging={lodging}
            lodgingPricesOptions={requiredLodgingPrices}
          />

          <SectionAddons
            campsites={campsites}
            lodging={lodging}
            lodgingPricesOptions={optionalLodgingPrices}
          />

          {landDetails && (
            <SectionLandDetails landDetails={landDetails} categories={activity} acreage={acreage} />
          )}

          <div>
            <ActivityFeed
              metaData={metadata}
              reviews={reviews}
              propertyVisit={propertyVisit}
              currentPackage={currentPackage}
              listing={currentListing}
              rootClassName={css.activityFeedRoot}
              isListing={false}
              activityPackageFeedData={activityPackageFeedData}
              fetchActivityFeedData={fetchActivityFeedData}
              fetchFollowedSubjectsData={fetchFollowedSubjectsData}
              followedSubjects={followedSubjects}
              followSubjectMutation={followSubjectMutation}
              unfollowSubjectMutation={unfollowSubjectMutation}
              updateFollowedSubjectMutation={updateFollowedSubjectMutation}
              currentUser={currentUser}
              fetchTotalLikesData={fetchTotalLikesData}
            />
          </div>

          {isHunting && (
            <SectionHuntingInformation
              publicData={publicData}
              currentPackage={currentPackage}
              currentListing={currentListing}
              licenseLink={licenseLink}
              regulationsLink={regulationsLink}
              showRegulationsLink={shouldShowRegulationsLink}
            />
          )}

          <SectionPropertyRules publicData={publicData} intl={intl} />
          <SectionPropertyAccess publicData={publicData} intl={intl} />

          <SectionReviews
            customerReviews={customerReviews}
            reviews={reviews}
            reviewsMeta={reviewsMeta}
            isFetchingReviews={isFetchingReviews}
          />

          <SectionFAQ faqs={faqsToRender} />

          <SectionMapMaybe
            geolocation={geolocation}
            publicData={publicData}
            listingId={currentListing.id}
          />

          <BuckChat
            pageType="package"
            listingId={rawParams.id}
            packageId={rawParams.packageId}
            currentUser={currentUser}
            inlineEntryTitle="Have a question about this package?"
            placeholder="Ask anything about this package..."
            inlineEntryTags={[
              'What species can I hunt?',
              'What are the property rules?',
              'Is lodging included?',
              'What weapons are allowed?',
            ]}
          />

          <SectionHostMaybe
            listing={currentListing}
            onContactUser={() => onContactUser(isOwnListing)}
            currentUser={currentUser}
            fullProfile
            showRate
            isEarlyAccess={isEarlyAccess}
            isContactDisabled={!isAvailable || outOfBookingRange}
            earlyAccessTimer={
              isEarlyAccess
                ? {
                    expirationTimestamp: earlyAccessExpiration,
                    labelText: userHasPremiumMembership ? (
                      <span>
                        You can message this landowner now with a LandTrust+ subscription.
                      </span>
                    ) : (
                      <span>
                        Early Access is available for{' '}
                        <b>
                          <a
                            href={config.landtrustPlusUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={css.landtrustPlusLink}
                          >
                            LandTrust+ members only
                          </a>
                        </b>
                        .
                      </span>
                    ),
                    descriptionText: userHasPremiumMembership
                      ? null
                      : `You'll be able to message ${authorFirstName} in:`,
                    totalDuration: config.packagesEarlyAccessDays * 24 * 60 * 60,
                  }
                : null
            }
          />
          <div className={css.hostBadgeWrapper}>{renderAvailabilityBadges()}</div>

          {usState && usCounty && <SEOBreadcrumbs breadcrumbs={seoBreadcrumbs} />}
        </div>
        <div className={css.secondaryContent}>
          <div>
            <PackagePricingPanel
              currentUserInit={currentUserInit}
              packageInfo={currentPackage}
              includedLodging={includedLodging}
              optionalLodging={optionalLodging}
              listing={currentListing}
              timeSlots={timeSlots}
              startingPrice={startingPrice}
              currentUser={currentUser}
              sticky
              transaction={transaction}
              availabilityPackageBadges={renderAvailabilityBadges(true)}
              isAvailable={isAvailable}
              outOfBookingRange={outOfBookingRange}
              bookingRangeIsFuture={bookingRangeIsFuture}
              isBlockedByEarlyAccess={isBlockedByEarlyAccess}
              formattedPackageAvailableDate={formattedPackageAvailableDate}
              onManageDisableScrolling={onManageDisableScrolling}
              isOnWaitingList={isOnWaitingList}
              setIsPackageAvailabilityModalOpen={setIsPackageAvailabilityModalOpen}
              setIsEarlyAccessModalOpen={setIsEarlyAccessModalOpen}
              setIsCredovaModalOpen={setIsCredovaModalOpen}
              onPriceChange={handlePriceChange}
              showCredova
              landownerFirstName={authorFirstName}
              isOwnListing={isOwnListing}
              onMessage={selection => onContactUser(isOwnListing, selection)}
              onBookingSelectionChange={setBookingSelection}
            />
          </div>

          <CredovaModal
            isOpen={isCredovaModalOpen}
            onCloseModal={() => setIsCredovaModalOpen(false)}
          />

          <EarlyAccessModal
            isOpen={isEarlyAccessModalOpen}
            onClose={() => {
              setIsEarlyAccessModalOpen(false);
            }}
          />

          <div>
            <MobileCheckAvailability
              currentUserInit={currentUserInit}
              isAvailable={isAvailable}
              outOfBookingRange={outOfBookingRange}
              startingPrice={startingPrice}
              currentTransaction={transaction}
              isOnWaitingList={isOnWaitingList}
              setIsPackageAvailabilityModalOpen={setIsPackageAvailabilityModalOpen}
              rawParams={rawParams}
              isBlockedByEarlyAccess={isBlockedByEarlyAccess}
              earlyAccessExpiration={earlyAccessExpiration}
              setIsEarlyAccessModalOpen={setIsEarlyAccessModalOpen}
              setIsCredovaModalOpen={setIsCredovaModalOpen}
              estimatedTotalPrice={estimatedTotalPrice}
              packageInfo={currentPackage}
              landownerFirstName={authorFirstName}
              isOwnListing={isOwnListing}
              onMessage={() => onContactUser(isOwnListing, bookingSelection)}
            />
          </div>
        </div>
      </Container>

      {/* BuckChat mounts ChatWidget internally */}
    </PackagePageWrapper>
  );
};

PackagePageComponent.defaultProps = {
  currentUser: null,
  showListingError: null,
};

PackagePageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  showListingError: propTypes.error,
};

const mapStateToProps = (state, ownProps) => {
  const { isAuthenticated } = state.Auth;
  const {
    showListingError,
    reviews,
    customerReviews,
    reviewsMeta,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    fetchingReviewsDataInProgress,
  } = state.ListingPage;
  const { currentUser, updatingWaitingListInProgress, currentUserInit } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const isOnWaitingList = isPackageOnWaitingList(currentUser, ownProps?.params?.packageId);

  let transactionIdFromInquiry = getTransactionFromInquiry(
    currentUser,
    ownProps?.params?.packageId
  );

  const { transactionRef } = state.Transaction;
  const transactions = getMarketplaceEntities(state, transactionRef ? [transactionRef] : []);

  // Make sure that we have transaction in pending inquiries
  const transaction =
    transactions.length > 0
      ? transactions.find(tx => tx.id.uuid === transactionIdFromInquiry) || null
      : null;

  if (!transaction) {
    transactionIdFromInquiry = null;
  }

  return {
    isAuthenticated,
    currentUserInit,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    showListingError,
    reviews,
    customerReviews,
    reviewsMeta,
    fetchReviewsError,
    timeSlots,
    fetchTimeSlotsError,
    transaction: transactionIdFromInquiry ? transaction : null,
    transactionIdFromInquiry,
    isOnWaitingList,
    updatingWaitingListInProgress,
    activityPackageFeedData: state.ListingPage.activityPackageFeedData,
    followedSubjects: state.user.followedSubjects,
    reviewsByIds: state.ListingPage.reviewsByIds,
    isFetchingReviews: fetchingReviewsDataInProgress,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onToggleFavoritePackage: (pkg, listing) => dispatch(toggleFavoritePackages(pkg, listing)),
  onFetchTransaction: txId => dispatch(fetchTransaction(txId)),
  onUpdateWaitingList: (packageId, listingId) => dispatch(updateWaitingList(packageId, listingId)),
  fetchActivityFeedData: (packageId, limit, offset) =>
    dispatch(fetchPackageActivityFeed(packageId, limit, offset)),
  fetchTotalLikesData: updateId => dispatch(fetchActivityLogLikes(updateId)),
  fetchFollowedSubjectsData: userId => dispatch(fetchFollowedSubjects(userId)),
  followSubjectMutation: (userId, subjectId, notifyBy) =>
    dispatch(followSubject(userId, subjectId, notifyBy)),
  unfollowSubjectMutation: (id, userId) => dispatch(unfollowSubject(id, userId)),
  updateFollowedSubjectMutation: (id, notifyBy) => dispatch(updateFollowedSubject(id, notifyBy)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const PackagePage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(PackagePageComponent);

PackagePage.setInitialValues = initialValues => setInitialValues(initialValues);
PackagePage.loadData = loadData;

export default PackagePage;
